import React, { useEffect, useState } from "react";
import { Box, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFetchTable } from "@hooks/useFetchDataTable";
import BackgroundImage from "@components/BackgroundImageCst";
import Table from "@components/Table";
import HeroCst from "@components/HeroCst";
import Breadcrumb from "@components/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import { ServicesURL } from "@utils/utilsApi";
import ToastCst from "@components/ToastCst";
import { LOType, PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import ButtonBackPage from "@components/ButtonBackPage";
import useSWRMutation from "swr/mutation";
import { MethodType } from "src/services/type";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import {
  useFetchDataDetail,
  useFetchDetail,
  usePostDetail,
} from "@hooks/useFetchDetail";
import AnimationFadeIn from "@components/AnimationFadeIn";
import AssociationTableCst from "@components/AssociationTableCst";
import BackButtonWarning from "@components/BackButtonWarning";
import { User } from "src/interfaces/User";
import ModalConfirmsCst from "@components/ModalConfirmsCst";

const UsersAssociationDashboard: React.FC = () => {
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    key?: string | number;
  }>();
  const { dashboardId, id = "1" } = useParams();
  const [usersAssociation, setUsersAssociation] = useState<any>(null);
  const [showTable, setShowTable] = useState(false);
  const { t } = useTranslation([
    "dashboard-users-association",
    "toast-message",
  ]);
  const navigate = useNavigate();
  const { dataDetail } = useFetchDataDetail(
    `${ServicesURL.getAssociations}?dashboardAssociationType=USERS&dashboardId=${dashboardId}&corporateId=${id}`,
    PagesID["dashboard.corporate-association"],
  );

  const { trigger } = usePostDetail(ServicesURL.associateUsers);

  const onUsersAssociation = async (selected: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToUsersAssociation: any = Object.keys(selected).map(
      (key: string) => {
        if (key.includes("||")) {
          return {
            id: key.split("||")[0],
            label: key.split("||")[1],
          };
        }
      },
    );

    const initialSelectionFormatted = objToUsersAssociation.map((item: any) => {
      return Number(item.id);
    });

    const listToSend = objToUsersAssociation.filter(
      (obj1: any) => !dataDetail.some((obj2: any) => obj1.id == obj2.id),
    );

    const modifiedList = listToSend.map((item: any) => {
      return Number(item.id);
    });

    const idsExist = dataDetail.map((item: any) => item.id);
    const idsToAdd = modifiedList.filter(
      (item: any) => !idsExist.includes(Number(item)),
    );

    const idsToDelete = idsExist.filter(
      (item: any) => !initialSelectionFormatted.includes(item),
    );

    const toSend: any = [...idsToAdd, ...idsToDelete];

    const objToSend: any = {
      corporate: {
        corporateId: id,
      },
      dashboardId,
      userIds: toSend,
    };

    if (!toSend.length) {
      setOpenToast({ s: StatusEnum.OK, message: "" });
      return;
    }

    try {
      await trigger(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      console.error(error);
      setOpenToast(undefined);
    }
  };

  useEffect(() => {
    const selected = dataDetail.map((data: any) => {
      return {
        id: data.id,
        name: data.label,
      };
    });

    setUsersAssociation(selected);
    setTimeout(() => {
      setShowTable(true);
    }, 500);
  }, [dataDetail]);

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-activities.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst
          title={`${t("hero-title")}`}
          description={t("hero-description")}
        />
        {!!showTable && (
          <AssociationTableCst
            association={usersAssociation}
            setAssociation={null}
            callBackAssociation={onUsersAssociation}
            setCloseAssociation={null}
            service={`${ServicesURL.getCustomer}?corporate_id=${id}`}
            pageID={PagesID["dashboard.corporate.customers"]}
            title={""}
            enableMultiRowSelection={true}
            customBackAction={() => navigate(-1)}
            labelKey="name"
          />
        )}
        <ModalConfirmsCst
          open={!!openToast}
          title={t("hero-title-add")}
          description={""}
          onCallBackClosed={() => {
            setOpenToast(undefined);
          }}
          status={openToast}
        />
      </Container>
    </AnimationFadeIn>
  );
};

export default UsersAssociationDashboard;
